import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Upload, FileDown, Trash2, Sparkles, Save, FileText, Loader2,
  FolderOpen, Plus, History, Download,
} from "lucide-react";
import { toast } from "sonner";

/**
 * XDR Documents tab.
 *
 * Two things live here:
 *   1. **Document library** — per-customer DOCX/PDF/XLSX/PPTX files grouped
 *      by category (Security Profile, Improvement Register, Tuning &
 *      Exceptions, generated Review Reports, Other). Full upload / download
 *      / delete.
 *   2. **Build review report** — upload the latest Barracuda executive PDF
 *      (+ optional dark-web PDF), Claude extracts the structured JSON,
 *      operator edits, generated PPTX is auto-archived into the library.
 *
 * Historical review reports (JSON) are fed back to Claude on every new run
 * so the generated narrative calls out trends.
 */

const CATEGORY_META = {
  security_profile:    { label: "Customer Security Profile",   group: "customer" },
  improvement_register:{ label: "Security Improvement Register",group: "customer" },
  tuning_exceptions:   { label: "Tuning & Exceptions Register",group: "customer" },
  other:               { label: "Other",                       group: "customer" },
  review_report:       { label: "Review Report",               group: "reports"  },
};

export default function XdrReviewTab({ accountId, accountName }) {
  // ---- Build-report state (same as before) ----
  const [files, setFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState(null);
  const [tokensInTemplate, setTokensInTemplate] = useState([]);
  const [spec, setSpec] = useState(null);
  const [additionalContext, setAdditionalContext] = useState("");
  const [usage, setUsage] = useState(null);
  const [historicalCount, setHistoricalCount] = useState(0);
  const fileInputRef = useRef(null);

  // ---- Document library state ----
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("security_profile");
  const [uploadPeriod, setUploadPeriod] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef(null);

  const loadDocs = async () => {
    setDocsLoading(true);
    try {
      const r = await api.get(`/accounts/${accountId}/xdr-documents`);
      setDocs(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load XDR documents");
    } finally { setDocsLoading(false); }
  };
  useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [accountId]);

  const grouped = useMemo(() => {
    const g = { security_profile: [], improvement_register: [], tuning_exceptions: [],
                other: [], review_report: [] };
    for (const d of docs) {
      if (g[d.category] !== undefined) g[d.category].push(d);
      else g.other.push(d);
    }
    return g;
  }, [docs]);

  // ---- Build-report handlers ---------------------------------------------
  const onDrop = (e) => { e.preventDefault(); addFiles(e.dataTransfer.files); };
  const addFiles = (list) => {
    const next = [...files];
    for (const f of list) {
      const n = f.name.toLowerCase();
      if (!n.endsWith(".pdf") && !n.endsWith(".csv")) {
        toast.error(`${f.name} skipped — only PDFs (Barracuda) and CSVs (Flare) are supported`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const extract = async () => {
    if (!files.length) return toast.error("Attach at least the Barracuda Executive PDF");
    setExtracting(true);
    try {
      // Step 1 — upload each PDF individually to object storage. Doing
      // this one-at-a-time keeps every request small/fast so Cloudflare
      // doesn't drop a 2-PDF multipart upload with a 520.
      const sourceIds = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        toast.info(`Uploading ${f.name} (${i + 1}/${files.length})…`);
        const form = new FormData();
        form.append("file", f);
        const up = await api.post(`/accounts/${accountId}/xdr-sources`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (!up.data?.id) throw new Error(`Upload for ${f.name} returned no id`);
        sourceIds.push(up.data.id);
      }

      // Step 2 — kick off the background extraction job. Small JSON POST,
      // returns a job id immediately regardless of PDF size.
      const start = await api.post(`/accounts/${accountId}/xdr-reviews/extract`, {
        source_ids: sourceIds,
        additional_context: (additionalContext || "").trim() || null,
      });
      const jobId = start.data.job_id;
      if (!jobId) throw new Error("Backend didn't return a job id");
      toast.info("AI extraction started — this usually takes 1-3 minutes for a full-template run.");

      // Step 3 — poll every 2s until done, max 10 minutes
      const started = Date.now();
      let finalResult = null;
      while (Date.now() - started < 10 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/xdr-extract-jobs/${jobId}`);
        const s = poll.data?.status;
        if (s === "complete") { finalResult = poll.data?.result; break; }
        if (s === "failed") {
          throw new Error(poll.data?.error || "Extraction failed on the server");
        }
      }
      if (!finalResult) {
        throw new Error("Extraction timed out after 10 minutes — the server is still working. Reload the page in a few minutes and check the Document Library.");
      }

      setData(finalResult.data || {});
      setTokensInTemplate(finalResult.tokens_in_template || []);
      setSpec(finalResult.spec || null);
      setUsage(finalResult.usage || null);
      setHistoricalCount(finalResult.historical_reviews_used || 0);
      const bits = [];
      if (finalResult.spec) {
        const s = finalResult.spec;
        bits.push(`${(s.text_fills || []).length} text · ${(s.tables || []).length} tables · ${(s.charts || []).length} charts`);
      }
      if (finalResult.historical_reviews_used)
        bits.push(`${finalResult.historical_reviews_used} prior review${finalResult.historical_reviews_used === 1 ? "" : "s"} referenced`);
      if (finalResult.additional_context_used)
        bits.push("additional context included");
      toast.success(
        bits.length
          ? `AI extraction done · ${bits.join(" · ")}`
          : "AI extraction complete — review and edit the details below",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Extraction failed");
    } finally { setExtracting(false); }
  };

  const downloadDraft = async () => {
    if (!data) return toast.error("Run AI extraction first");
    setDownloading(true);
    try {
      const r = await api.post(
        `/accounts/${accountId}/xdr-reviews/render`,
        { customer_name: data.customer_name || accountName, data },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `XDR Review - ${(data.customer_name || accountName).trim()}.pptx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Draft PowerPoint downloaded (not yet archived — use 'Save & archive' to keep it on the account)");
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* ignore */ }
      }
      toast.error(detail || "Render failed");
    } finally { setDownloading(false); }
  };

  const saveAndArchive = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.post(`/accounts/${accountId}/xdr-reviews`, {
        customer_name: data.customer_name || accountName,
        period_label: data.period_label || null,
        data,
      });
      toast.success("Review archived — PPTX is now in the Document Library");
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // ---- Document library handlers -----------------------------------------
  const openUploadModal = (category) => {
    setUploadCategory(category || "security_profile");
    setUploadPeriod("");
    setUploadFiles([]);
    setUploadOpen(true);
  };
  const onUploadDrop = (e) => { e.preventDefault(); addUploadFiles(e.dataTransfer.files); };
  const addUploadFiles = (list) => {
    const next = [...uploadFiles];
    const MAX = 25 * 1024 * 1024;
    for (const f of list) {
      if (f.size > MAX) {
        toast.error(`${f.name} is larger than 25 MB — split or compress first`);
        continue;
      }
      next.push(f);
    }
    setUploadFiles(next);
  };
  const submitUpload = async () => {
    if (!uploadFiles.length) return toast.error("Attach at least one file");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("category", uploadCategory);
      if (uploadPeriod) form.append("period_label", uploadPeriod);
      uploadFiles.forEach((f) => form.append("files", f));
      await api.post(`/accounts/${accountId}/xdr-documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${uploadFiles.length} file${uploadFiles.length === 1 ? "" : "s"} uploaded`);
      setUploadOpen(false);
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/xdr-documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/xdr-documents/${doc.id}`);
      toast.success("Deleted");
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ---- helpers -----------------------------------------------------------
  const h = data?.headline || {};
  const updateHeadline = (k, v) =>
    setData({ ...data, headline: { ...h, [k]: v === "" ? null : (isNaN(Number(v)) ? v : Number(v)) } });
  const updateField = (k, v) => setData({ ...data, [k]: v });

  return (
    <div className="space-y-6" data-testid="xdr-documents-tab">
      {/* ============ DOCUMENT LIBRARY ============ */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-blue-600" /> Document Library
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              Customer-specific XDR docs and archived review reports for <span className="font-medium text-slate-700">{accountName}</span>. The AI references the last four review reports when drafting new ones for trend analysis.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => openUploadModal("security_profile")} data-testid="xdr-upload-open-btn">
            <Plus className="h-4 w-4 mr-1" /> Upload document
          </Button>
        </CardHeader>
        <CardContent className="p-4 space-y-5" data-testid="xdr-doc-library">
          {/* Customer documents group */}
          <section>
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Customer documents</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["security_profile", "improvement_register", "tuning_exceptions", "other"].map((cat) => (
                <CategoryList
                  key={cat}
                  category={cat}
                  label={CATEGORY_META[cat].label}
                  items={grouped[cat] || []}
                  onUpload={() => openUploadModal(cat)}
                  onDownload={downloadDoc}
                  onDelete={deleteDoc}
                  loading={docsLoading}
                />
              ))}
            </div>
          </section>

          {/* Review reports group */}
          <section>
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1">
              <History className="h-3.5 w-3.5" /> Review reports (used for AI trend analysis)
            </div>
            <CategoryList
              category="review_report"
              label="Archived review reports"
              items={grouped.review_report || []}
              onUpload={() => openUploadModal("review_report")}
              onDownload={downloadDoc}
              onDelete={deleteDoc}
              loading={docsLoading}
              wide
            />
          </section>
        </CardContent>
      </Card>

      {/* ============ BUILD REVIEW REPORT ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-blue-600" /> Build review report
              </CardTitle>
              <div className="text-xs text-slate-500 mt-1">
                Upload the Barracuda Executive report (and optional dark-web monitoring PDF). Claude extracts the key numbers and drafts the narrative — you review and edit before the deck is generated. The period label on the report (e.g. "September 2026" or "Q3 2026 (July – September)") carries the cadence.
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500 hover:border-blue-300 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="xdr-dropzone"
              >
                <Upload className="h-6 w-6 mx-auto mb-2 text-slate-400" />
                Drop Barracuda PDFs or Flare CSVs here, or click to browse
                <input ref={fileInputRef} type="file"
                       accept="application/pdf,text/csv,application/csv,.csv,.pdf"
                       multiple
                       className="hidden" onChange={(e) => addFiles(e.target.files)} />
              </div>
              {files.length > 0 && (
                <ul className="text-xs text-slate-700 space-y-1" data-testid="xdr-file-list">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5">
                      <span className="truncate flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-slate-400" />{f.name}</span>
                      <button className="text-slate-400 hover:text-red-600"
                              onClick={() => setFiles(files.filter((_, ix) => ix !== i))}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div>
                <Label className="text-xs flex items-center gap-1">
                  Additional context for AI <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Textarea
                  rows={3}
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Anything you want the AI to weave into the narrative. e.g. 'Customer onboarded in July so the data window is shorter', 'Mention the OneDrive restore pilot that ran this period', 'They're migrating off legacy Exchange next quarter'."
                  className="mt-1 text-xs"
                  data-testid="xdr-additional-context"
                />
              </div>
              <Button onClick={extract} disabled={extracting || !files.length}
                      className="w-full gap-2 bg-blue-600 hover:bg-blue-700" data-testid="xdr-extract-btn">
                {extracting ? <><Loader2 className="h-4 w-4 animate-spin" /> Extracting…</> : <><Sparkles className="h-4 w-4" /> Extract with AI</>}
              </Button>
              {usage && (
                <div className="text-[11px] text-slate-500">
                  Model: <span className="font-mono">{usage.model}</span> · {usage.input_tokens} in / {usage.output_tokens} out tokens
                  {historicalCount > 0 && <> · <span className="text-emerald-700">{historicalCount} prior review{historicalCount === 1 ? "" : "s"} referenced for trends</span></>}
                </div>
              )}
            </CardContent>
          </Card>

          {data && (
            <Card className="border-slate-200" data-testid="xdr-preview">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-heading">Review &amp; edit</CardTitle>
                <div className="text-xs text-slate-500 mt-1">
                  AI filled every placeholder, table and chart in the active template. Edit any value — your changes land in the final deck exactly as typed here. Nothing in the template without a placeholder is touched.
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-5">
                {/* Deck-fill editor: text / tables / charts grouped by slide */}
                <DeckFillEditor
                  data={data}
                  spec={spec}
                  onChange={setData}
                />

                {/* Legacy structured fields — hidden by default now that tokens
                    drive the deck. Kept for historical trend tracking. */}
                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-700 font-semibold text-slate-700 text-sm">
                    Structured history fields <span className="text-slate-400 font-normal">— used for AI trend analysis next run</span>
                  </summary>
                  <div className="mt-3 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div><Label className="text-xs">Customer name</Label><Input value={data.customer_name || ""} onChange={(e) => updateField("customer_name", e.target.value)} data-testid="xdr-customer" /></div>
                      <div><Label className="text-xs">Period label</Label><Input value={data.period_label || ""} onChange={(e) => updateField("period_label", e.target.value)} placeholder="Q3 2026 (July – September) or September 2026" data-testid="xdr-period" /></div>
                      <div><Label className="text-xs">Report date</Label><Input value={data.report_date || ""} onChange={(e) => updateField("report_date", e.target.value)} placeholder="30 Sep 2026" /></div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-700 mb-2">Headline numbers</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {[
                          ["alerts_issued_this_quarter", "Alerts this period"],
                          ["alerts_resolved_in_sla", "Resolved in SLA"],
                          ["incidents_critical", "Critical"],
                          ["incidents_high", "High"],
                          ["incidents_medium", "Medium"],
                          ["incidents_low", "Low"],
                          ["mean_time_to_respond_minutes", "MTTR (min)"],
                          ["dark_web_findings", "Dark web"],
                        ].map(([k, label]) => (
                          <div key={k}>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
                            <Input value={h[k] ?? ""} onChange={(e) => updateHeadline(k, e.target.value)} data-testid={`xdr-h-${k}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">SOC overview summary</Label>
                      <Textarea rows={3} value={data.soc_overview_summary || ""} onChange={(e) => updateField("soc_overview_summary", e.target.value)} data-testid="xdr-soc-summary" />
                    </div>
                    <div>
                      <Label className="text-xs">Recommendations <span className="text-slate-400">(one per line)</span></Label>
                      <Textarea rows={4}
                        value={(data.recommendations || []).join("\n")}
                        onChange={(e) => updateField("recommendations", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                        data-testid="xdr-recommendations" />
                    </div>
                  </div>
                </details>

                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-700">Raw JSON (advanced)</summary>
                  <pre className="mt-2 p-3 bg-slate-50 rounded border border-slate-200 overflow-x-auto text-[11px]">{JSON.stringify(data, null, 2)}</pre>
                </details>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="border-slate-200 sticky top-4">
            <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Actions</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-2">
              <Button className="w-full gap-2 bg-blue-600 hover:bg-blue-700" onClick={saveAndArchive} disabled={saving || !data} data-testid="xdr-save-btn">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Save className="h-4 w-4" /> Save &amp; archive</>}
              </Button>
              <Button variant="outline" className="w-full gap-2" onClick={downloadDraft} disabled={downloading || !data} data-testid="xdr-download-btn">
                {downloading ? <><Loader2 className="h-4 w-4 animate-spin" /> Rendering…</> : <><FileDown className="h-4 w-4" /> Download draft (no archive)</>}
              </Button>
              <div className="text-[11px] text-slate-500 pt-1">
                "Save &amp; archive" stores the PPTX in the Document Library so future reports can reference it for trend analysis. The master template still contains some pilot-customer references in prose body sections — edit the final deck for any remaining tweaks.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ============ UPLOAD DIALOG ============ */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="xdr-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload XDR document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="xdr-upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="security_profile">Customer Security Profile</SelectItem>
                  <SelectItem value="improvement_register">Security Improvement Register</SelectItem>
                  <SelectItem value="tuning_exceptions">Tuning &amp; Exceptions Register</SelectItem>
                  <SelectItem value="review_report">Review Report (manual upload)</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Period label <span className="text-slate-400">(optional — e.g. September 2026)</span></Label>
              <Input value={uploadPeriod} onChange={(e) => setUploadPeriod(e.target.value)} placeholder="September 2026" data-testid="xdr-upload-period" />
            </div>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onUploadDrop}
              onClick={() => uploadInputRef.current?.click()}
              className="border-2 border-dashed border-slate-200 rounded-lg p-5 text-center text-sm text-slate-500 hover:border-blue-300 cursor-pointer"
              data-testid="xdr-upload-dropzone"
            >
              <Upload className="h-5 w-5 mx-auto mb-2 text-slate-400" />
              Drop files here, or click to browse (max 25 MB each)
              <input ref={uploadInputRef} type="file" multiple className="hidden"
                     onChange={(e) => addUploadFiles(e.target.files)} />
            </div>
            {uploadFiles.length > 0 && (
              <ul className="text-xs text-slate-700 space-y-1">
                {uploadFiles.map((f, i) => (
                  <li key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5">
                    <span className="truncate flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-slate-400" />{f.name} <span className="text-slate-400">· {formatSize(f.size)}</span></span>
                    <button className="text-slate-400 hover:text-red-600"
                            onClick={() => setUploadFiles(uploadFiles.filter((_, ix) => ix !== i))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={submitUpload} disabled={uploading || !uploadFiles.length}
                    className="bg-blue-600 hover:bg-blue-700" data-testid="xdr-upload-submit">
              {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Uploading…</> : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Sub-components --------------------------------------------------------

function CategoryList({ category, label, items, onUpload, onDownload, onDelete, loading, wide }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-white ${wide ? "" : ""}`} data-testid={`xdr-cat-${category}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <div className="text-sm font-medium text-slate-800">{label} <span className="text-xs text-slate-400 font-normal">· {items.length}</span></div>
        <button
          className="text-xs text-blue-700 hover:text-blue-900 flex items-center gap-1"
          onClick={onUpload}
          data-testid={`xdr-cat-upload-${category}`}
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {loading && items.length === 0 ? (
          <div className="p-3 text-xs text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-3 text-xs text-slate-400">No files yet.</div>
        ) : items.map((d) => (
          <div key={d.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-slate-800 flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="truncate">{d.name}</span>
              </div>
              <div className="text-[11px] text-slate-500">
                {d.period_label ? `${d.period_label} · ` : ""}
                {(d.created_at || "").slice(0, 10)} · {d.created_by} · {formatSize(d.size)}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button className="p-1 text-slate-500 hover:text-blue-700" title="Download"
                      onClick={() => onDownload(d)} data-testid={`xdr-doc-download-${d.id}`}>
                <Download className="h-4 w-4" />
              </button>
              <button className="p-1 text-slate-400 hover:text-red-600" title="Delete"
                      onClick={() => onDelete(d)} data-testid={`xdr-doc-delete-${d.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSize(bytes) {
  if (bytes == null) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}


// --- DeckFillEditor ---------------------------------------------------------
// Rich editor for the comprehensive deck-fill result. Groups items by slide
// so engineers can walk the deck top-down: text paragraphs → tables → charts.
// Any item without a resolved value is highlighted so missed fills are
// obvious before the operator hits Save.
function DeckFillEditor({ data, spec, onChange }) {
  const textFills = data?.text_fills || [];
  const tables = data?.tables || [];
  const charts = data?.charts || [];

  // Index Claude's fills by id for lookup against the spec
  const textById = React.useMemo(() => {
    const m = new Map();
    for (const t of textFills) m.set(t.id, t);
    return m;
  }, [textFills]);
  const tableById = React.useMemo(() => {
    const m = new Map();
    for (const t of tables) m.set(t.id, t);
    return m;
  }, [tables]);
  const chartById = React.useMemo(() => {
    const m = new Map();
    for (const c of charts) m.set(c.id, c);
    return m;
  }, [charts]);

  // Group spec items by slide so the editor reads top-down
  const slides = React.useMemo(() => {
    const bySlide = new Map();
    const addItem = (item, kind) => {
      const n = item.slide || 0;
      if (!bySlide.has(n)) bySlide.set(n, { slide: n, section: item.section || "", text: [], tables: [], charts: [] });
      bySlide.get(n)[kind].push(item);
    };
    for (const t of spec?.text_fills || []) addItem(t, "text");
    for (const t of spec?.tables || [])     addItem(t, "tables");
    for (const c of spec?.charts || [])     addItem(c, "charts");
    return Array.from(bySlide.values()).sort((a, b) => a.slide - b.slide);
  }, [spec]);

  const setResolved = (id, newResolved) => {
    const next = { ...data };
    const idx = (next.text_fills || []).findIndex((t) => t.id === id);
    if (idx >= 0) {
      next.text_fills = [...next.text_fills];
      next.text_fills[idx] = { ...next.text_fills[idx], resolved: newResolved };
    } else {
      next.text_fills = [...(next.text_fills || []), { id, resolved: newResolved }];
    }
    onChange(next);
  };
  const setTableRows = (id, rows) => {
    const next = { ...data };
    const idx = (next.tables || []).findIndex((t) => t.id === id);
    if (idx >= 0) {
      next.tables = [...next.tables];
      next.tables[idx] = { ...next.tables[idx], rows };
    } else {
      next.tables = [...(next.tables || []), { id, rows }];
    }
    onChange(next);
  };
  const setChart = (id, patch) => {
    const next = { ...data };
    const idx = (next.charts || []).findIndex((c) => c.id === id);
    const existing = idx >= 0 ? next.charts[idx] : { id, categories: [], series: [] };
    const merged = { ...existing, ...patch };
    if (idx >= 0) {
      next.charts = [...next.charts];
      next.charts[idx] = merged;
    } else {
      next.charts = [...(next.charts || []), merged];
    }
    onChange(next);
  };

  if (!spec || (!slides.length)) {
    return <div className="text-xs text-slate-500">No fillable items were discovered in the template.</div>;
  }

  const totals = {
    text: spec.text_fills?.length || 0,
    tables: spec.tables?.length || 0,
    charts: spec.charts?.length || 0,
  };

  return (
    <div className="space-y-4" data-testid="xdr-deck-fill-editor">
      <div className="rounded border border-blue-100 bg-blue-50/60 px-3 py-2 text-[11px] text-blue-900">
        AI filled <b>{totals.text}</b> text placeholders, <b>{totals.tables}</b> dynamic tables and <b>{totals.charts}</b> charts across <b>{slides.length}</b> slides. Red outlines flag anything Claude left blank.
      </div>

      {slides.map((s) => (
        <details key={s.slide} className="rounded-lg border border-slate-200 bg-white open:border-slate-300" open={s.slide <= 2}>
          <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-800 flex items-center justify-between">
            <span>Slide {s.slide} · <span className="text-slate-500 font-normal">{s.section || "Untitled section"}</span></span>
            <span className="text-[10px] text-slate-400 font-normal">
              {s.text.length ? `${s.text.length} text` : ""}{s.tables.length ? ` · ${s.tables.length} tables` : ""}{s.charts.length ? ` · ${s.charts.length} charts` : ""}
            </span>
          </summary>
          <div className="px-3 pb-3 space-y-3 border-t border-slate-100">
            {/* Text fills */}
            {s.text.map((tf) => {
              const resolved = textById.get(tf.id)?.resolved || "";
              const long = (resolved + tf.text).length > 90;
              const missing = !resolved;
              return (
                <div key={tf.id} className="mt-3">
                  <Label className="text-[11px] flex items-center gap-1">
                    <span className="text-slate-400 font-mono">{tf.id}</span>
                    <span className="text-slate-400 truncate">· template: <span className="font-mono">{tf.text}</span></span>
                  </Label>
                  {long ? (
                    <Textarea
                      rows={Math.min(6, Math.max(2, Math.ceil(resolved.length / 70)))}
                      value={resolved}
                      onChange={(e) => setResolved(tf.id, e.target.value)}
                      className={missing ? "border-red-300 focus-visible:ring-red-200" : ""}
                      data-testid={`xdr-text-${tf.id}`}
                    />
                  ) : (
                    <Input
                      value={resolved}
                      onChange={(e) => setResolved(tf.id, e.target.value)}
                      className={missing ? "border-red-300 focus-visible:ring-red-200" : ""}
                      data-testid={`xdr-text-${tf.id}`}
                    />
                  )}
                </div>
              );
            })}

            {/* Tables */}
            {s.tables.map((t) => (
              <TableEditor
                key={t.id}
                spec={t}
                rows={tableById.get(t.id)?.rows || []}
                onRowsChange={(rows) => setTableRows(t.id, rows)}
              />
            ))}

            {/* Charts */}
            {s.charts.map((c) => (
              <ChartEditor
                key={c.id}
                spec={c}
                chart={chartById.get(c.id)}
                onChange={(patch) => setChart(c.id, patch)}
              />
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}


function TableEditor({ spec, rows, onRowsChange }) {
  const cols = spec.columns || [];
  const addRow = () => onRowsChange([...rows, cols.map(() => "")]);
  const removeRow = (i) => onRowsChange(rows.filter((_, ix) => ix !== i));
  const updateCell = (ri, ci, v) => {
    const next = rows.map((r) => [...r]);
    while (next[ri].length < cols.length) next[ri].push("");
    next[ri][ci] = v;
    onRowsChange(next);
  };
  return (
    <div className="mt-3 rounded border border-slate-200" data-testid={`xdr-table-${spec.id}`}>
      <div className="flex items-center justify-between px-2 py-1.5 bg-slate-50 border-b border-slate-200">
        <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-2">
          <span className="font-mono text-slate-400">{spec.id}</span>
          <span>{spec.columns.join(" · ")}</span>
        </div>
        <button className="text-[11px] text-blue-700 hover:text-blue-900 flex items-center gap-0.5"
                onClick={addRow} data-testid={`xdr-table-${spec.id}-add`}>
          <Plus className="h-3 w-3" /> Add row
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-left">
              {cols.map((c) => (
                <th key={c} className="px-2 py-1 font-mono text-[10px] text-slate-500 uppercase">{c}</th>
              ))}
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={cols.length + 1} className="px-2 py-3 text-center text-xs text-slate-400">
                  AI returned no rows for this table — click "Add row" to add one manually, or leave empty to render an em-dash.
                </td>
              </tr>
            ) : rows.map((row, ri) => (
              <tr key={ri} className="border-t border-slate-100">
                {cols.map((_, ci) => (
                  <td key={ci} className="px-1 py-1 align-top">
                    <Input
                      value={row[ci] || ""}
                      onChange={(e) => updateCell(ri, ci, e.target.value)}
                      className="h-7 text-xs"
                      data-testid={`xdr-table-${spec.id}-r${ri}c${ci}`}
                    />
                  </td>
                ))}
                <td className="px-1 py-1 text-right">
                  <button className="text-slate-300 hover:text-red-600" onClick={() => removeRow(ri)}
                          data-testid={`xdr-table-${spec.id}-del${ri}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ChartEditor({ spec, chart, onChange }) {
  const cats = chart?.categories || [];
  const series = chart?.series || [];
  const updateCat = (i, v) => {
    const next = [...cats];
    next[i] = v;
    onChange({ categories: next, series });
  };
  const updateSeriesValue = (si, vi, v) => {
    const next = series.map((s) => ({ ...s, values: [...(s.values || [])] }));
    while (next[si].values.length < cats.length) next[si].values.push(0);
    const n = Number(v);
    next[si].values[vi] = isNaN(n) ? 0 : n;
    onChange({ categories: cats, series: next });
  };
  const updateSeriesName = (si, name) => {
    const next = series.map((s) => ({ ...s }));
    next[si].name = name;
    onChange({ categories: cats, series: next });
  };
  return (
    <div className="mt-3 rounded border border-slate-200" data-testid={`xdr-chart-${spec.id}`}>
      <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-700 flex items-center gap-2">
        <span className="font-mono text-slate-400">{spec.id}</span>
        <span>Chart: {spec.chart_type?.toString()?.split(" ")[0] || "chart"}</span>
      </div>
      <div className="p-2 space-y-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Categories</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {cats.map((c, i) => (
              <Input key={i} value={c} className="h-7 text-xs w-24" onChange={(e) => updateCat(i, e.target.value)}
                     data-testid={`xdr-chart-${spec.id}-cat${i}`} />
            ))}
          </div>
        </div>
        {series.map((s, si) => (
          <div key={si}>
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Series</Label>
              <Input value={s.name || ""} className="h-6 text-xs max-w-[180px]"
                     onChange={(e) => updateSeriesName(si, e.target.value)}
                     data-testid={`xdr-chart-${spec.id}-sname${si}`} />
            </div>
            <div className="flex flex-wrap gap-1">
              {(s.values || []).map((v, vi) => (
                <Input key={vi} type="number" value={v} className="h-7 text-xs w-24"
                       onChange={(e) => updateSeriesValue(si, vi, e.target.value)}
                       data-testid={`xdr-chart-${spec.id}-s${si}v${vi}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// --- TokenEditor (legacy – kept for templates still using the old token API)
function TokenEditor({ tokens, values, onChange }) {
  const isLongForm = (name) => /summary|narrative|list|commentary|paragraph|recommend|steps|notes|description/i.test(name);
  const prettyLabel = (name) => name.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

  return (
    <div data-testid="xdr-token-editor">
      <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
        <Sparkles className="h-3.5 w-3.5 text-blue-600" />
        AI-filled template fields <span className="text-slate-400 font-normal">({tokens.length})</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {tokens.map((name) => {
          const long = isLongForm(name);
          const v = values[name] ?? "";
          const field = long ? (
            <Textarea
              rows={3}
              value={v}
              onChange={(e) => onChange(name, e.target.value)}
              data-testid={`xdr-token-${name}`}
            />
          ) : (
            <Input
              value={v}
              onChange={(e) => onChange(name, e.target.value)}
              data-testid={`xdr-token-${name}`}
            />
          );
          return (
            <div key={name} className={long ? "md:col-span-2" : ""}>
              <Label className="text-[11px] flex items-center gap-1">
                <span className="font-mono text-slate-500">{`{{${name}}}`}</span>
                <span className="text-slate-400">· {prettyLabel(name)}</span>
              </Label>
              {field}
            </div>
          );
        })}
      </div>
    </div>
  );
}
